import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { formatKES } from '../../../services/invoiceService';
import { generateInvoicePDF } from '../../../utils/generateInvoicePDF';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-secondary-100 text-secondary-700', icon: 'FileText' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: 'Send' },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-700', icon: 'CheckCircle' },
  partially_paid: { label: 'Partial', color: 'bg-amber-100 text-amber-700', icon: 'Clock' },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: 'AlertCircle' },
  cancelled: { label: 'Cancelled', color: 'bg-secondary-100 text-secondary-500', icon: 'XCircle' }
};

const AGING_CONFIG = {
  'current': { label: 'Current', color: 'text-emerald-600' },
  '0-30': { label: '0-30 days', color: 'text-amber-600' },
  '31-60': { label: '31-60 days', color: 'text-orange-600' },
  '60+': { label: '60+ days', color: 'text-red-600' }
};

const InvoiceTable = ({ invoices, loading, onView, onEdit, onSend, onDelete, onRecordPayment }) => {
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sorted = [...(invoices || [])]?.sort((a, b) => {
    let aVal = a?.[sortField];
    let bVal = b?.[sortField];
    if (typeof aVal === 'string') aVal = aVal?.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal?.toLowerCase();
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ field }) => (
    <Icon
      name={sortField === field ? (sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
      size={14}
      className="ml-1 text-text-secondary"
    />
  );

  if (loading) {
    return (
      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="p-6 space-y-3">
          {[1,2,3,4,5]?.map(i => (
            <div key={i} className="h-12 bg-secondary-50 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!invoices?.length) {
    return (
      <div className="bg-surface rounded-xl border border-border p-12 text-center">
        <div className="w-16 h-16 bg-secondary-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="FileText" size={28} className="text-text-secondary" />
        </div>
        <h3 className="text-base font-heading-semibold text-text-primary mb-1">No invoices found</h3>
        <p className="text-sm text-text-secondary">Create your first invoice to get started</p>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-xl border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-secondary-50">
              {[
                { label: 'Invoice #', field: 'invoiceNumber' },
                { label: 'Client', field: 'caseTitle' },
                { label: 'Issue Date', field: 'issueDate' },
                { label: 'Due Date', field: 'dueDate' },
                { label: 'Status', field: 'status' },
                { label: 'Subtotal', field: 'subtotal' },
                { label: 'VAT (16%)', field: 'vatAmount' },
                { label: 'Total', field: 'total' },
                { label: 'Aging', field: 'agingCategory' },
                { label: 'Actions', field: null }
              ]?.map(col => (
                <th
                  key={col?.label}
                  className={`px-4 py-3 text-left text-xs font-body-medium text-text-secondary whitespace-nowrap ${col?.field ? 'cursor-pointer hover:text-text-primary select-none' : ''}`}
                  onClick={() => col?.field && handleSort(col?.field)}
                >
                  <span className="flex items-center">
                    {col?.label}
                    {col?.field && <SortIcon field={col?.field} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sorted?.map(invoice => {
              const statusCfg = STATUS_CONFIG?.[invoice?.status] || STATUS_CONFIG?.draft;
              const agingCfg = invoice?.agingCategory ? AGING_CONFIG?.[invoice?.agingCategory] : null;
              return (
                <tr key={invoice?.id} className="hover:bg-secondary-50 transition-colors">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onView?.(invoice)}
                      className="text-sm font-body-medium text-primary hover:underline"
                    >
                      {invoice?.invoiceNumber || '—'}
                    </button>
                    {invoice?.isLocked && (
                      <Icon name="Lock" size={12} className="ml-1 text-text-secondary inline" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-body-medium text-text-primary">{invoice?.client?.fullName || '—'}</div>
                    <div className="text-xs text-text-secondary">{invoice?.caseTitle}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                    {invoice?.issueDate ? new Date(invoice.issueDate)?.toLocaleDateString('en-KE') : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                    {invoice?.dueDate ? new Date(invoice.dueDate)?.toLocaleDateString('en-KE') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-body-medium ${statusCfg?.color}`}>
                      <Icon name={statusCfg?.icon} size={11} />
                      {statusCfg?.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                    {formatKES(invoice?.subtotal)}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                    {formatKES(invoice?.vatAmount)}
                  </td>
                  <td className="px-4 py-3 text-sm font-body-medium text-text-primary whitespace-nowrap">
                    {formatKES(invoice?.total)}
                  </td>
                  <td className="px-4 py-3">
                    {agingCfg ? (
                      <span className={`text-xs font-body-medium ${agingCfg?.color}`}>{agingCfg?.label}</span>
                    ) : <span className="text-xs text-text-secondary">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onView?.(invoice)}
                        className="p-1.5 rounded-lg hover:bg-secondary-100 text-text-secondary hover:text-primary transition-colors"
                        title="View invoice"
                      >
                        <Icon name="Eye" size={15} />
                      </button>
                      <button
                        onClick={() => generateInvoicePDF(invoice)}
                        className="p-1.5 rounded-lg hover:bg-secondary-100 text-text-secondary hover:text-emerald-600 transition-colors"
                        title="Download PDF"
                      >
                        <Icon name="Download" size={15} />
                      </button>
                      {!invoice?.isLocked && (
                        <button
                          onClick={() => onEdit?.(invoice)}
                          className="p-1.5 rounded-lg hover:bg-secondary-100 text-text-secondary hover:text-primary transition-colors"
                          title="Edit invoice"
                        >
                          <Icon name="Edit2" size={15} />
                        </button>
                      )}
                      {invoice?.status === 'draft' && (
                        <button
                          onClick={() => onSend?.(invoice)}
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-text-secondary hover:text-blue-600 transition-colors"
                          title="Send invoice"
                        >
                          <Icon name="Send" size={15} />
                        </button>
                      )}
                      {['sent', 'partially_paid', 'overdue']?.includes(invoice?.status) && (
                        <button
                          onClick={() => onRecordPayment?.(invoice)}
                          className="p-1.5 rounded-lg hover:bg-emerald-50 text-text-secondary hover:text-emerald-600 transition-colors"
                          title="Record payment"
                        >
                          <Icon name="CreditCard" size={15} />
                        </button>
                      )}
                      {invoice?.status === 'draft' && (
                        <button
                          onClick={() => onDelete?.(invoice)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-text-secondary hover:text-red-600 transition-colors"
                          title="Delete invoice"
                        >
                          <Icon name="Trash2" size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoiceTable;
